import {Clusterconfig} from "./clusterconfig";
import {Tokenstorage} from "./tokenstorage";
import {logMessage, getAppStateVersion, getDiagnostics} from "../taskpane";
import 'cross-fetch/polyfill';

export class NotarisdossierApi
{

    /**
     * @param username
     * @param password
     * @param office
     * @returns {Promise<unknown>}
     */
    static authenticate(username, password, office)
    {
        logMessage('NotarisdossierApi.authenticate()');
        return new Promise(function(resolve, reject) {
            const authenticateUrl = Clusterconfig.getUrl() + '/v3/rest/application/authenticate';
            const requestedScope = NotarisdossierApi.requestedScope();
            logMessage('-> Send POST to ' + authenticateUrl);
            let body = JSON.stringify({username: username, password: password, scope: requestedScope});
            if (office !== null) {
                body = JSON.stringify({username: username, password: password, scope: requestedScope, office_id: office});
            }

            fetch(authenticateUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-App-Name': 'Outlook add-in',
                    'X-App-Version': getAppStateVersion(),
                    'X-App-Diagnostics': getDiagnostics(),
                },
                body: body
            }).then((response) => {
                logMessage('-> Received response with status ' + response.status);
                return response.json();
            }).then((json) => {
                if (json.hasOwnProperty('message')) {
                    if (json.message === 'Authentication ambiguous') {
                        reject(json);
                    }
                    reject('Onjuiste combinatie van gebruikersnaam en wachtwoord');
                } else {
                    Tokenstorage.setTokens(json.access_token, json.refresh_token);
                }
                resolve();
            }).catch((e) => {
                logMessage(e);
                reject('Kan niet verbinden met server');
            });
        });
    }

    /**
     * @returns {string[]}
     */
    static requestedScopes()
    {
        return [
            'config:read',
            'file.internaldocuments:write',
        ];
    }

    /**
     * @returns {string}
     */
    static requestedScope()
    {
        return NotarisdossierApi.requestedScopes().join(',');
    }

    /**
     * @returns {boolean}
     */
    static accessTokenHasRequestedScopes()
    {
        return Tokenstorage.accessTokenHasScopes(NotarisdossierApi.requestedScopes());
    }

    /**
     * @returns {{reauthenticationRequired: boolean, message: string}}
     */
    static createReauthenticationRequiredError()
    {
        return {
            reauthenticationRequired: true,
            message: 'Meld eenmalig opnieuw aan om e-mails te kunnen opslaan',
        };
    }


    /**
     * @param code
     * @param extOfficeUuid
     * @returns {Promise<void>}
     */
    static async exchangePlatformHandoff(code, extOfficeUuid)
    {
        logMessage('NotarisdossierApi.exchangePlatformHandoff()');
        const exchangeUrl = Clusterconfig.getUrl() + '/v4/auth/outlook-addin/platform-handoff';
        logMessage('-> Send POST to ' + exchangeUrl);

        const response = await fetch(exchangeUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-App-Name': 'Outlook add-in',
                'X-App-Version': getAppStateVersion(),
                'X-App-Diagnostics': getDiagnostics(),
            },
            body: JSON.stringify({
                code: code,
                extOfficeUuid: extOfficeUuid,
                scope: NotarisdossierApi.requestedScope(),
            }),
        });

        logMessage('-> Received response with status ' + response.status);
        const json = await NotarisdossierApi.parseJsonResponse(response);

        if (response.status !== 200) {
            throw NotarisdossierApi.createHttpError(response, json, 'Aanmelden met SSO is niet gelukt');
        }

        if (!json.access_token || !json.refresh_token) {
            throw 'Aanmelden met SSO is niet gelukt';
        }

        Tokenstorage.setTokens(json.access_token, json.refresh_token);
    }


    /**
     * @returns {Promise<unknown>}
     */
    static refreshTokens()
    {
        logMessage('NotarisdossierApi.refreshTokens()');
        if (!NotarisdossierApi.accessTokenHasRequestedScopes()) {
            return Promise.reject(NotarisdossierApi.createReauthenticationRequiredError());
        }

        return new Promise(function(resolve, reject) {
            const refreshTokenUrl = Clusterconfig.getUrl() + '/v3/rest/application/refresh-token';
            logMessage('-> Send POST to ' + refreshTokenUrl);
            fetch(refreshTokenUrl, {
                method: 'POST',
                body: JSON.stringify({refresh_token: Tokenstorage.getRefreshToken()}),
                headers: {
                    'Content-Type': 'application/json',
                    'X-App-Name': 'Outlook add-in',
                    'X-App-Version': getAppStateVersion(),
                    'X-App-Diagnostics': getDiagnostics(),
                },
            }).then((response) => {
                logMessage('-> Received response with status ' + response.status);
                if (response.status == 200) {
                    return response.json();
                } else if (response.status == 401) {
                    // This means refresh tokens are not valid, logout
                    reject(response);
                } else {
                    reject('error while refreshing tokens');
                }
            }).then((json) => {
                Tokenstorage.setTokens(json.access_token, json.refresh_token);
                if (!NotarisdossierApi.accessTokenHasRequestedScopes()) {
                    throw NotarisdossierApi.createReauthenticationRequiredError();
                }
                resolve();
            }).catch((error) => {
                if (error !== null && error !== undefined && error.reauthenticationRequired) {
                    reject(error);
                } else {
                    reject('unknown error');
                }
            });
        });
    }


    /**
     * @returns {Promise<void>}
     */
    static async refreshTokenIfNeeded()
    {
        if (!NotarisdossierApi.accessTokenHasRequestedScopes()) {
            throw NotarisdossierApi.createReauthenticationRequiredError();
        }

        if (Tokenstorage.isAccessTokenExpired()) {
            await NotarisdossierApi.refreshTokens();
        };
    }


    /**
     * Folder names that we want to fetch
     */
    static folderNameSettings = [
        'folder-name-emails',
        'folder-name-correspondence',
        'folder-name-drafts',
        'folder-name-instrument-copy',
        'folder-name-financial',
        'folder-name-identity-cards',
        'folder-name-identity-check',
        'folder-name-kadaster',
        'folder-name-kadaster-registration',
        'folder-name-kvk',
        'folder-name-uploaded-docs',
        'folder-name-instrument-original',
        'folder-name-registers',
        'folder-name-vis',
        'folder-name-wwft-check',
    ];


    /**
      * @returns {Promise<unknown>}
     */
    static async getSettings(settings)
    {
        await NotarisdossierApi.refreshTokenIfNeeded();

        logMessage('NotarisdossierApi.getSettings()');
        return new Promise(function(resolve, reject) {
            let getFoldersUrl = Clusterconfig.getUrl() + '/v3/rest/application/setting';
            logMessage('-> Send GET to ' + getFoldersUrl);
            getFoldersUrl += '/' + settings.join();
            fetch(getFoldersUrl, {
                method: 'GET',
                credentials: 'include',
                headers: {Authorization: 'Bearer ' + Tokenstorage.getAccessToken()},
            }).then((response) => {
                logMessage('-> Received response with status ' + response.status);
                if (response.status === 200) {
                    return response.json();
                } else {
                    reject('error while getting folder config')
                }
            }).then((json) => {
                resolve(json)
            }).catch(() => {
                reject('unknown error');
            });
        });
    }


    /**
     * @param term
     * @param isEmailSubject
     * @returns {Promise<unknown>}
     */
    static async searchFile(term, isEmailSubject = false)
    {
        await NotarisdossierApi.refreshTokenIfNeeded();

        logMessage('NotarisdossierApi.searchFile()');
        return new Promise(function(resolve, reject) {
            const queryParams = '?' + (isEmailSubject ? 'email-subject' : 'search') + '=' + encodeURIComponent(term);
            const searchFileUrl = Clusterconfig.getUrl() + '/v3/rest/core/file' + queryParams;
            logMessage('-> Send GET to ' + searchFileUrl);
            fetch(searchFileUrl, {
                method: 'GET',
                credentials: 'include',
                headers: {'Authorization': 'Bearer ' + Tokenstorage.getAccessToken()},
            }).then((response) => {
                logMessage('-> Received response with status ' + response.status);
                if (response.status === 200) {
                    return response.json();
                } else {
                    reject('Error bij het ophalen van dossiers');
                }
            }).then((json) => {
                resolve(json)
            }).catch(() => {
                reject('unknown error');
            });
        });
    }


    static async parseJsonResponse(response)
    {
        const text = await response.text();
        if (text === '') {
            return {};
        }

        try {
            return JSON.parse(text);
        } catch (e) {
            return {};
        }
    }


    static createHttpError(response, json, fallbackMessage)
    {
        let message = fallbackMessage;
        if (json !== null && json !== undefined) {
            if (json.message !== undefined) {
                message = json.message;
            } else if (json.error !== undefined && json.error.message !== undefined) {
                message = json.error.message;
            }
        }

        return {
            status: response.status,
            message: message,
            response: json
        };
    }


    /**
     * @param mailbox
     * @returns {Promise<unknown>}
     */
    static async getOutlookMessageCapability(mailbox = null)
    {
        await NotarisdossierApi.refreshTokenIfNeeded();

        logMessage('NotarisdossierApi.getOutlookMessageCapability()');
        return new Promise(function(resolve, reject) {
            let capabilityUrl = Clusterconfig.getUrl() + '/v3/rest/document/outlook-message/capability';
            if (mailbox !== null && mailbox !== undefined && mailbox !== '') {
                capabilityUrl += '?mailbox=' + encodeURIComponent(mailbox);
            }
            logMessage('-> Send GET to ' + capabilityUrl);
            fetch(capabilityUrl, {
                method: 'GET',
                credentials: 'include',
                headers: {'Authorization': 'Bearer ' + Tokenstorage.getAccessToken()},
            }).then(async (response) => {
                logMessage('-> Received response with status ' + response.status);
                const json = await NotarisdossierApi.parseJsonResponse(response);
                if (response.status === 200) {
                    resolve(json);
                } else {
                    reject(NotarisdossierApi.createHttpError(response, json, 'Fout bij ophalen Outlook Graph configuratie'));
                }
            }).catch((e) => {
                logMessage(e);
                reject({message: 'Kan niet verbinden met server'});
            });
        });
    }


    /**
     * @param params
     * @returns {Promise<unknown>}
     */
    static async saveOutlookMessage(params)
    {
        await NotarisdossierApi.refreshTokenIfNeeded();

        logMessage('NotarisdossierApi.saveOutlookMessage()');
        logMessage('-> File: ' + params.file_uuid);
        logMessage('-> Name: ' + params.name);
        logMessage('-> Folder: ' + params.folder);
        logMessage('-> Mailbox: ' + params.mailbox);
        logMessage('-> Message ID type: ' + params.message_id_type);

        return new Promise(function(resolve, reject) {
            const saveOutlookMessageUrl = Clusterconfig.getUrl() + '/v3/rest/document/outlook-message';
            logMessage('-> Send POST to ' + saveOutlookMessageUrl);
            fetch(saveOutlookMessageUrl, {
                method: 'POST',
                body: JSON.stringify(params),
                credentials: 'include',
                headers: {
                    'Authorization': 'Bearer ' + Tokenstorage.getAccessToken(),
                    'Content-Type': 'application/json'
                },
            }).then(async (response) => {
                logMessage('-> Received response with status ' + response.status);
                const json = await NotarisdossierApi.parseJsonResponse(response);
                if (response.status === 200 || response.status === 201 || response.status === 204) {
                    resolve(json);
                } else {
                    reject(NotarisdossierApi.createHttpError(response, json, 'Fout bij opslaan e-mail via Outlook Graph'));
                }
            }).catch((e) => {
                logMessage(e);
                reject({message: 'Kan niet verbinden met server'});
            });
        });
    }


    /**
     * @param fileUuid
     * @param name
     * @param folderName
     * @param content
     * @param isBase64
     * @param metadata
     * @returns {Promise<unknown>}
     */
    static async addFileInternalDocument(fileUuid, name, folderName, content, isBase64, metadata = {})
    {
        await NotarisdossierApi.refreshTokenIfNeeded();
        logMessage('NotarisdossierApi.addFileInternalDocument()');
        logMessage('-> File: ' + fileUuid);
        logMessage('-> Name: ' + name);
        logMessage('-> Folder: ' + folderName);
        if (metadata !== null && metadata !== undefined && Object.keys(metadata).length > 0) {
            logMessage('-> Metadata: ' + JSON.stringify(metadata));
        }

        return new Promise(function(resolve, reject) {
            const addInternalDocumentUrl = Clusterconfig.getUrl() + '/v3/rest/document/file-internal-document';
            let params = {
                file_uuid: fileUuid,
                name: name,
                folder: folderName,
                content: content,
                base64: isBase64
            };
            if (metadata !== null && metadata !== undefined && Object.keys(metadata).length > 0) {
                ['source', 'save_method', 'legacy_reason', 'mailbox'].forEach((key) => {
                    if (metadata[key] !== undefined && metadata[key] !== null) {
                        params[key] = metadata[key];
                    }
                });
            }
            logMessage('-> Send POST to ' + addInternalDocumentUrl);
            fetch(addInternalDocumentUrl, {
                method: 'POST',
                body: JSON.stringify(params),
                credentials: 'include',
                headers: {
                    'Authorization': 'Bearer ' + Tokenstorage.getAccessToken(),
                    'Content-Type': 'application/json'
                },
            }).then((response) => {
                logMessage('-> Received response with status ' + response.status);
                if (response.status === 201) {
                    resolve();
                } else {
                    reject('Error bij het opslaan van bestand')
                }
            }).catch((e) => {
                logMessage(e);
                reject({message: 'Kan niet verbinden met server'});
            });
        });
    }


}
